import { ArrowDown, ArrowUp } from 'lucide-react';
import type { CustomerPortfolioSortKey } from '../api/customerManagerWorkspace.ts';
import { useT } from '../i18n/index.ts';
import type { PortfolioSort } from '../pages/customerPortfolioSort.ts';
import { portfolioSortForKey } from '../pages/customerPortfolioSort.ts';
import { IconButton, Select } from '../ui/index.ts';

/** Radix Select forbids an empty value. */
const UNSORTED_VALUE = '__default';

export interface PortfolioSortColumn {
  readonly key: CustomerPortfolioSortKey;
  readonly label: string;
}

interface Props {
  readonly sort: PortfolioSort | null;
  readonly columns: readonly PortfolioSortColumn[];
  readonly onSortChange: (sort: PortfolioSort | null) => void;
  /** When true a leading "default order" option maps to `null` (the workbench keeps the server's historical order). */
  readonly allowUnsorted?: boolean;
  readonly testIdPrefix: string;
}

export function PortfolioSortControl({
  sort,
  columns,
  onSortChange,
  allowUnsorted = false,
  testIdPrefix,
}: Props): JSX.Element {
  const t = useT();
  const options = [
    ...(allowUnsorted
      ? [{ value: UNSORTED_VALUE, label: t('mobile.sort.unsorted') }]
      : []),
    ...columns.map((column) => ({ value: column.key, label: column.label })),
  ];

  function handleValueChange(value: string): void {
    if (value === UNSORTED_VALUE) {
      onSortChange(null);
      return;
    }
    const column = columns.find((candidate) => candidate.key === value);
    if (column === undefined) {
      return;
    }
    onSortChange(portfolioSortForKey(column.key));
  }

  function flipDirection(): void {
    if (sort === null) {
      return;
    }
    onSortChange({ key: sort.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-label text-paper-low">{t('mobile.sort.label')}</span>
      <Select
        value={sort === null ? UNSORTED_VALUE : sort.key}
        onValueChange={handleValueChange}
        options={options}
        ariaLabel={t('mobile.sort.aria_label')}
        className="min-w-0 flex-1"
        testId={`${testIdPrefix}-sort-select`}
      />
      <IconButton
        label={t(sort?.dir === 'asc' ? 'mobile.sort.dir_asc' : 'mobile.sort.dir_desc')}
        disabled={sort === null}
        onClick={flipDirection}
        data-testid={`${testIdPrefix}-sort-dir`}
      >
        {sort?.dir === 'asc' ? (
          <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
        )}
      </IconButton>
    </div>
  );
}
